import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Label} from '@/components/ui/label/Label'
import {
    SelectComboboxInput,
    SelectComboboxPopover,
    SelectItem,
    SelectList,
    SelectTrigger
} from '@/components/ui/select-atoms/SelectAtoms'
import {ComboboxProvider, SelectProvider} from '@ariakit/react'
import {AslDistrict, City} from '@/types'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {InputStatusIcon} from '@/components/ui/input-status-icon/InputStatusIcon'
import {useEffect, useMemo, useRef} from 'react'
import {StyledAslDistrictTrigger, StyledDistrict, StyledSelectItemAslDistrict} from './style'
import {useTranslation} from '@/translations/i18n'
import {useGetAslByCity} from '@/services/useGetAslByCity'
import {Optional} from '@tanstack/react-query'
import {useGetCities} from '@/services/useGetCities'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'

interface InputSelectAslProps {
    label: string
    value?: AslDistrict | null
    onChange: (value: AslDistrict | null) => void
    placeholder?: string
    city?: Optional<City, 'id'> | null
    errorMessage?: string
    isPortal?: boolean
}

export const InputSelectAsl = ({
    label,
    placeholder,
    value,
    onChange,
    city,
    errorMessage,
    isPortal
}: InputSelectAslProps) => {
    const {t} = useTranslation()
    const inputAslRef = useRef(null)

    // retrieve the city id if is not provided to enable the asl search
    const citiesQuery = useGetCities(
        {
            search: city?.name,
            responseType: 'extended',
            limit: 1
        },
        {enabled: !!city?.name}
    )

    const cityId = useMemo(() => city?.id || citiesQuery?.remappedData[0]?.id, [city, citiesQuery.remappedData])

    const {
        remappedData: aslDistricts,
        onSearch,
        onResetSearch,
        isFetching,
        isError
    } = useGetAslByCity({
        cityId: cityId ?? '',
        responseType: 'extended',
        options: {
            enabled: !!cityId
        }
    })

    // booleans
    const isOutOfRegion = useMemo(
        () => city && aslDistricts.length == 0 && !isFetching && !isError,
        [city, aslDistricts, isFetching, isError]
    )

    // side effects
    useEffect(() => {
        if (value && !city) {
            onChange(null)
        }
    }, [value, city])

    return (
        <ComboboxProvider setValue={onSearch} resetValueOnHide>
            <SelectProvider
                value={value?.id ?? ''}
                setValue={value =>
                    aslDistricts && onChange(aslDistricts.find(aslDistrict => value == aslDistrict.id) ?? null)
                }
                setOpen={() => onResetSearch(inputAslRef)}
            >
                <Flexbox direction="column" gap={1.5} fullWidth>
                    <Label>{label}</Label>
                    <Flexbox direction="column" gap={1.5} fullWidth>
                        <SelectTrigger
                            placeholder={isOutOfRegion ? t('commons:out_region') : placeholder}
                            fullWidth
                            value={value?.name}
                            disabled={isOutOfRegion == true}
                            hasError={!!errorMessage}
                            displayValue={() => (
                                <StyledAslDistrictTrigger gap={1}>
                                    {value?.asl?.name}
                                    <StyledSelectItemAslDistrict>{value?.name}</StyledSelectItemAslDistrict>
                                </StyledAslDistrictTrigger>
                            )}
                        />

                        {isFetching && <Skeleton height={20} />}
                        {city && aslDistricts.length > 0 && (
                            <StyledDistrict>{t('users:asl_city_domain', {city: city.name})}</StyledDistrict>
                        )}
                        {isOutOfRegion && <StyledDistrict>{t('commons:asl_location_not_found')}</StyledDistrict>}
                    </Flexbox>

                    <Flexbox align="center" gap={errorMessage ? 1.5 : 0}>
                        <InputStatusIcon hasError={!!errorMessage} />
                        <InputHelpText error={errorMessage} />
                    </Flexbox>

                    <SelectComboboxPopover portal={!!isPortal}>
                        <SelectComboboxInput ref={inputAslRef} placeholder={t('commons:search_district_placeholder')} />
                        <SelectList>
                            {isFetching ? (
                                <>
                                    {Array.from({length: 6}, (_, index) => (
                                        <Skeleton key={index} height={32} />
                                    ))}
                                </>
                            ) : isError ? (
                                <InputHelpText error={t('errors:default')} />
                            ) : aslDistricts && aslDistricts.length > 0 ? (
                                aslDistricts.map(district => (
                                    <SelectItem hasItemCheckbox key={district.id} value={district.id}>
                                        {district.asl?.name}{' '}
                                        <StyledSelectItemAslDistrict>{district.name}</StyledSelectItemAslDistrict>
                                    </SelectItem>
                                ))
                            ) : (
                                <InputHelpText helpText={t('commons:no_results')} />
                            )}
                        </SelectList>
                    </SelectComboboxPopover>
                </Flexbox>
            </SelectProvider>
        </ComboboxProvider>
    )
}
